import React, {useEffect, useState} from 'react';
import {Icon, Modal} from 'antd';

import './style.css';


function InstructionImageModal({
                                   visible,
                                   onCancel,
                                   children
                               }) {
    return (
        <Modal
            className="instruction-image-modal"
            visible={visible}
            footer={null}
            width="92vw"
            style={{top: 24}}
            bodyStyle={{
                padding: '42px 12px 12px',
                textAlign: 'center'
            }}
            maskClosable
            destroyOnClose
            onCancel={onCancel}
        >
            {children}
        </Modal>
    );
}


function InstructionImageHint() {
    return (
        <span className="instruction-image-button__hint">
            <Icon type="zoom-in"/>

            <span>
                Увеличить
            </span>
        </span>
    );
}


export function InstructionImage({src, alt}) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <button
                className="instruction-image-button"
                type="button"
                aria-label={`Увеличить изображение: ${alt}`}
                onClick={() => setIsOpen(true)}
            >
                <img
                    className="instruction-image"
                    src={src}
                    alt={alt}
                />

                <InstructionImageHint/>
            </button>

            <InstructionImageModal
                visible={isOpen}
                onCancel={() => setIsOpen(false)}
            >
                <img
                    className="instruction-image-modal__image"
                    src={src}
                    alt={alt}
                />
            </InstructionImageModal>
        </>
    );
}


function normalizeArea(area) {
    const top = Math.max(0, Math.min(100, area.top));
    const left = Math.max(0, Math.min(100, area.left));
    const width = Math.max(
        0,
        Math.min(100 - left, area.width)
    );
    const height = Math.max(
        0,
        Math.min(100 - top, area.height)
    );

    return {
        top,
        left,
        width,
        height
    };
}


function getSpotlightStyle(area) {
    const normalizedArea = normalizeArea(area);

    return {
        top: `${normalizedArea.top}%`,
        left: `${normalizedArea.left}%`,
        width: `${normalizedArea.width}%`,
        height: `${normalizedArea.height}%`
    };
}


function InstructionSpotlightImage({
                                       src,
                                       alt,
                                       activeArea,
                                       fallbackArea
                                   }) {
    const [isOpen, setIsOpen] = useState(false);
    const [lastArea, setLastArea] = useState(
        activeArea || fallbackArea
    );

    useEffect(() => {
        if (activeArea) {
            setLastArea(activeArea);
        }
    }, [activeArea]);

    const viewportClassName = [
        'instruction-spotlight__viewport',
        activeArea
            ? 'instruction-spotlight__viewport--active'
            : ''
    ].filter(Boolean).join(' ');

    return (
        <>
            <button
                className="instruction-image-button"
                type="button"
                aria-label={`Увеличить изображение: ${alt}`}
                onClick={() => setIsOpen(true)}
            >
                <span className="instruction-spotlight__card">
                    <span className={viewportClassName}>
                        <img
                            className="instruction-spotlight__image"
                            src={src}
                            alt={alt}
                        />

                        <span
                            className="instruction-spotlight__focus"
                            style={getSpotlightStyle(lastArea)}
                            aria-hidden="true"
                        />
                    </span>
                </span>

                <InstructionImageHint/>
            </button>

            <InstructionImageModal
                visible={isOpen}
                onCancel={() => setIsOpen(false)}
            >
                <img
                    className="instruction-image-modal__image"
                    src={src}
                    alt={alt}
                />
            </InstructionImageModal>
        </>
    );
}


export function InstructionSpotlight({
                                         src,
                                         alt,
                                         items = [],
                                         layout = 'list'
                                     }) {
    const [hoveredItem, setHoveredItem] = useState(null);
    const [focusedItem, setFocusedItem] = useState(null);
    const activeItemId = hoveredItem || focusedItem;
    const activeItem = items.find(
        item => item.id === activeItemId
    );
    const checklistClassName = [
        'service-checklist',
        layout === 'grid'
            ? 'service-checklist--grid'
            : ''
    ].filter(Boolean).join(' ');

    if (items.length === 0) {
        return (
            <div className="instruction-media-list">
                <div className="instruction-media-item">
                    <InstructionImage
                        src={src}
                        alt={alt}
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="instruction-media-list">
                <div className="instruction-media-item">
                    <InstructionSpotlightImage
                        src={src}
                        alt={alt}
                        activeArea={activeItem && activeItem.area}
                        fallbackArea={items[0].area}
                    />
                </div>
            </div>

            <div className={checklistClassName}>
                {items.map((item) => {
                    const isActive = activeItemId === item.id;
                    const className = [
                        'service-checklist__item',
                        'service-checklist__item--spotlight',
                        'service-checklist__item--interactive',
                        isActive
                            ? 'service-checklist__item--active'
                            : ''
                    ].filter(Boolean).join(' ');

                    return (
                        <div
                            className={className}
                            key={item.id}
                            role="button"
                            tabIndex={0}
                            aria-pressed={isActive}
                            onMouseEnter={() => setHoveredItem(item.id)}
                            onMouseLeave={() => setHoveredItem(null)}
                            onFocus={() => setFocusedItem(item.id)}
                            onBlur={() => setFocusedItem(null)}
                            onClick={event => {
                                event.currentTarget.focus();
                            }}
                            onKeyDown={event => {
                                if (
                                    event.key === 'Enter' ||
                                    event.key === ' '
                                ) {
                                    event.preventDefault();
                                    event.currentTarget.focus();
                                }
                            }}
                        >
                            <span className="service-checklist__number">
                                {item.number}
                            </span>

                            <p className="service-checklist__text">
                                {item.content}
                            </p>

                            <span className="instruction-spotlight-action">
                                <Icon type="eye"/>

                                <span>
                                    Показать
                                </span>
                            </span>
                        </div>
                    );
                })}
            </div>
        </>
    );
}


export function InstructionStep({
                                    number,
                                    title,
                                    children
                                }) {
    return (
        <div className="instruction-step">
            <div className="instruction-step__number">
                <div className="instruction-step__number-value">
                    {number}
                </div>

                <div className="instruction-step__body">
                    <p className="instruction-step__text">
                        <strong>
                            {title}
                        </strong>
                    </p>

                    {children}
                </div>
            </div>
        </div>
    );
}


export default function InstructionLayout({
                                              title,
                                              children
                                          }) {
    return (
        <article className="instruction-layout">
            <h1 className="instruction-layout__title">
                {title}
            </h1>

            <div className="instruction-layout__content">
                {children}
            </div>
        </article>
    );
}